import { lookupTown } from "./postcodesApi";

// Everything marketOverview() and advertiserOverview() need for one job, read
// with whichever Supabase client is passed in ("db"): the app passes its
// signed-in browser client (reportApi.js), the MCP server its own. Both load
// exactly the same rows, so the dashboard and the MCP can never disagree.
// No React and no browser-only code, so the MCP Worker can import it.
// reportLoader.test.js checks the dashboard's data and queries stay the same.

// { job, runs, samples, searches, appearances, typeOverrides, listingLinks, towns, now }
// agencyId: the account whose business type corrections and listing links
// apply. The app's database rules already limit these to the login's account;
// the MCP (service key, no rules) must pass it.
export async function loadReportData(db, jobId, { agencyId } = {}) {
  const [job, runs, samples, searches, appearances, typeOverrides, listingLinks] = await Promise.all([
    loadJobWithNiche(db, jobId),
    loadRows(db, "runs", jobId, "run_number"),
    loadRows(db, "samples", jobId, "sample_number"),
    loadSearches(db, jobId),
    loadAppearances(db, jobId),
    loadTypeOverrides(db, agencyId),
    loadListingLinks(db, agencyId),
  ]);
  const towns = job ? await townsForJob(job) : {};
  return { job, runs, samples, searches, appearances, typeOverrides, listingLinks, towns, now: new Date().toISOString() };
}

// Supabase sends at most 1,000 rows per request.
const PAGE_SIZE = 1000;

// Every row of a job's table, a page at a time, oldest first.
async function loadPaged(db, table, columns, jobId, label) {
  const all = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await db
      .from(table)
      .select(columns)
      .eq("job_id", jobId)
      .order("created_at", { ascending: true })
      .order("id", { ascending: true })
      .range(from, from + PAGE_SIZE - 1);
    if (error && label) console.error(label, error);
    if (!data || data.length === 0) break;
    all.push(...data);
    if (data.length < PAGE_SIZE) break;
  }
  return all;
}

// Every appearance saved for a job.
export function loadAppearances(db, jobId) {
  return loadPaged(db, "appearances", "*", jobId, null);
}

// Each search's recorded outcome (jobs from 14 Oct 2026 on). Older jobs have
// none, and the report works their outcomes out instead.
function loadSearches(db, jobId) {
  return loadPaged(db, "searches", "id, sample_id, run_id, keyword, postcode, device, status, ads_call, search_call", jobId, "fetchSearches");
}

// The job, with its niche and sub-group names.
async function loadJobWithNiche(db, jobId) {
  const { data, error } = await db
    .from("jobs")
    .select("*, niches(name), sub_groups(name)")
    .eq("id", jobId)
    .single();
  if (!error) return data;

  // If the names can't be joined, the report still works without them.
  console.error("fetchJobWithNiche", error);
  const plain = await db.from("jobs").select("*").eq("id", jobId).single();
  return plain.data;
}

async function loadRows(db, table, jobId, orderBy) {
  const { data, error } = await db.from(table).select("*").eq("job_id", jobId).order(orderBy);
  if (error) console.error(`fetch ${table}`, error);
  return data || [];
}

// Approved business type corrections only: [{ key, type }]. With agencyId,
// only that account's.
export async function loadTypeOverrides(db, agencyId) {
  let query = db.from("advertiser_types").select("key, type").eq("status", "approved");
  if (agencyId) query = query.eq("agency_id", agencyId);
  const { data, error } = await query;
  if (error) console.error("fetchTypeOverrides", error);
  return data || [];
}

// Applied map pack listing links only: [{ listing_key, domain, action, status }].
// With agencyId, only that account's.
// If the table isn't there yet, the report still works without links.
export async function loadListingLinks(db, agencyId) {
  let query = db
    .from("advertiser_links")
    .select("listing_key, domain, action, status")
    .eq("status", "applied");
  if (agencyId) query = query.eq("agency_id", agencyId);
  const { data, error } = await query;
  if (error) console.error("fetchListingLinks", error);
  return data || [];
}

// ---------- Towns ----------

// { "HD3": "Huddersfield", ... }: the towns saved on the job when it was
// scanned, and a postcodes.io lookup only for postcodes without one (jobs
// from before 14 Oct 2026).
async function townsForJob(job) {
  const saved = {};
  for (const p of Array.isArray(job.postcodes) ? job.postcodes : []) {
    if (p && p.postcode && p.town) saved[p.postcode] = p.town;
  }
  const missing = (job.locations || []).filter(p => !saved[p]);
  return { ...(missing.length > 0 ? await lookupTowns(missing) : {}), ...saved };
}

// Towns from postcodes.io, remembered until the page is reloaded (or, in the
// MCP Worker, for as long as Cloudflare keeps it running).
const townCache = new Map();
const TOWN_LOOKUPS_AT_ONCE = 10;

// { "S1": "Sheffield", ... } - postcodes with no town are left out.
export async function lookupTowns(postcodes) {
  const missing = [...new Set(postcodes)].filter(p => !townCache.has(p));
  for (let i = 0; i < missing.length; i += TOWN_LOOKUPS_AT_ONCE) {
    const batch = missing.slice(i, i + TOWN_LOOKUPS_AT_ONCE);
    const found = await Promise.all(batch.map(p => lookupTown(p)));
    batch.forEach((p, j) => townCache.set(p, found[j] || null));
  }
  const towns = {};
  for (const p of postcodes) if (townCache.get(p)) towns[p] = townCache.get(p);
  return towns;
}
